import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { Button } from "../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Calendar,
  Mail,
  Phone,
  User,
  Clock,
  FileText,
  RefreshCw,
  Trash2,
  Download,
  Eye,
  Briefcase,
  CheckCircle,
  XCircle,
  Link as LinkIcon,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Admin() {
  const [searchParams] = useSearchParams();
  const [appointments, setAppointments] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [taxIntakes, setTaxIntakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIntake, setSelectedIntake] = useState(null);
  const [calendarStatus, setCalendarStatus] = useState({ connected: false });
  const [connectingCalendar, setConnectingCalendar] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [appointmentsRes, contactsRes, intakesRes, calendarRes] = await Promise.all([
        axios.get(`${API}/appointments`),
        axios.get(`${API}/contacts`),
        axios.get(`${API}/tax-intakes`),
        axios.get(`${API}/oauth/calendar/status`),
      ]);
      setAppointments(appointmentsRes.data);
      setContacts(contactsRes.data);
      setTaxIntakes(intakesRes.data);
      setCalendarStatus(calendarRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    
    // Check for calendar connection result from URL
    const calendarParam = searchParams.get('calendar');
    if (calendarParam === 'success') {
      toast.success("Google Calendar connected successfully!");
    } else if (calendarParam === 'error') {
      toast.error("Failed to connect Google Calendar. Please try again.");
    }
  }, [searchParams]);

  const connectGoogleCalendar = async () => {
    setConnectingCalendar(true);
    try {
      const response = await axios.get(`${API}/oauth/calendar/login`);
      window.location.href = response.data.authorization_url;
    } catch (error) {
      console.error("Error connecting calendar:", error);
      toast.error("Failed to start calendar connection");
      setConnectingCalendar(false);
    }
  };

  const disconnectGoogleCalendar = async () => {
    if (!window.confirm("Are you sure you want to disconnect Google Calendar?")) return;
    try {
      await axios.post(`${API}/oauth/calendar/disconnect`);
      setCalendarStatus({ connected: false });
      toast.success("Google Calendar disconnected");
    } catch (error) {
      console.error("Error disconnecting calendar:", error);
      toast.error("Failed to disconnect calendar");
    }
  };

  const deleteAppointment = async (id) => {
    if (!window.confirm("Are you sure you want to delete this appointment?")) return;
    try {
      await axios.delete(`${API}/appointments/${id}`);
      setAppointments(appointments.filter((apt) => apt.id !== id));
      toast.success("Appointment deleted");
    } catch (error) {
      console.error("Error deleting appointment:", error);
      toast.error("Failed to delete appointment");
    }
  };

  const getServiceName = (serviceId) => {
    const serviceNames = {
      "tax-services": "Tax Services",
      "tax-preparation": "Tax Preparation",
      "tax-consultation": "Tax Consultation",
      "business-tax": "Business Tax",
      "bookkeeping": "Bookkeeping",
      "payroll": "Payroll Services",
      "website-design": "Website Design",
      "other": "Other",
      "immigration-notary": "Immigration & Notary",
      "recruitment-staffing": "Recruitment & Staffing",
      "credit-repair": "Credit Repair",
      "business-development": "Business Development",
      "website-ai": "Website Design & AI",
      "travel-booking": "Travel & Booking",
      "admin-services": "Administrative Services",
    };
    return serviceNames[serviceId] || serviceId;
  };

  const getFilingStatusLabel = (status) => {
    const labels = {
      single: "Single",
      married_jointly: "Married Filing Jointly",
      married_separately: "Married Filing Separately",
      head_of_household: "Head of Household",
    };
    return labels[status] || status;
  };

  const getEmploymentTypeLabel = (type) => {
    const labels = {
      w2: "W-2 Employee",
      self_employed: "Self-Employed / 1099",
      both: "Both W-2 and Self-Employed",
    };
    return labels[type] || type;
  };

  return (
    <div data-testid="admin-page" className="min-h-screen pt-20 bg-[#F9F9F7]">
      {/* Header */}
      <section className="py-12 bg-[#0F392B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white">
                Admin Dashboard
              </h1>
              <p className="font-body text-white/70 mt-2">
                Manage bookings, intake forms, and inquiries
              </p>
            </div>
            <div className="flex items-center gap-4">
              <Button
                onClick={fetchData}
                disabled={loading}
                data-testid="refresh-btn"
                className="bg-[#D4AF37] text-[#0F392B] hover:bg-[#D4AF37]/90 rounded-full px-6"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Google Calendar Connection Card */}
          <div className="bg-white rounded-xl p-6 shadow-lg mb-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${calendarStatus.connected ? 'bg-green-100' : 'bg-stone-100'}`}>
                  <Calendar className={`w-6 h-6 ${calendarStatus.connected ? 'text-green-600' : 'text-[#4A5568]'}`} />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-[#0F392B]">
                    Google Calendar Sync
                  </h3>
                  <p className="font-body text-sm text-[#4A5568]">
                    {calendarStatus.connected 
                      ? `Connected to ${calendarStatus.email}` 
                      : "Connect to automatically add appointments to your calendar"
                    }
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {calendarStatus.connected ? (
                  <>
                    <span className="flex items-center gap-2 text-green-600 text-sm font-medium">
                      <CheckCircle className="w-4 h-4" />
                      Connected
                    </span>
                    <Button
                      onClick={disconnectGoogleCalendar}
                      variant="outline"
                      size="sm"
                      className="text-red-500 border-red-200 hover:bg-red-50"
                    >
                      Disconnect
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={connectGoogleCalendar}
                    disabled={connectingCalendar}
                    data-testid="connect-calendar-btn"
                    className="bg-[#0F392B] text-white hover:bg-[#0F392B]/90 rounded-full"
                  >
                    <LinkIcon className="w-4 h-4 mr-2" />
                    {connectingCalendar ? "Connecting..." : "Connect Calendar"}
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-[#0F392B]/10 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-[#0F392B]" />
                </div>
                <div>
                  <p className="font-body text-sm text-[#4A5568]">Appointments</p>
                  <p className="font-heading text-3xl font-bold text-[#0F392B]">
                    {appointments.length}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-[#0F392B]" />
                </div>
                <div>
                  <p className="font-body text-sm text-[#4A5568]">Tax Intakes</p>
                  <p className="font-heading text-3xl font-bold text-[#0F392B]">
                    {taxIntakes.length}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                  <Mail className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="font-body text-sm text-[#4A5568]">Contact Inquiries</p>
                  <p className="font-heading text-3xl font-bold text-[#0F392B]">
                    {contacts.length}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <Tabs defaultValue="appointments" className="bg-white rounded-2xl shadow-lg p-6">
            <TabsList className="mb-6 flex-wrap">
              <TabsTrigger value="appointments" data-testid="tab-appointments">
                <Calendar className="w-4 h-4 mr-2" />
                Appointments ({appointments.length})
              </TabsTrigger>
              <TabsTrigger value="intakes" data-testid="tab-intakes">
                <FileText className="w-4 h-4 mr-2" />
                Tax Intakes ({taxIntakes.length})
              </TabsTrigger>
              <TabsTrigger value="contacts" data-testid="tab-contacts">
                <Mail className="w-4 h-4 mr-2" />
                Contacts ({contacts.length})
              </TabsTrigger>
            </TabsList>

            {/* Appointments Tab */}
            <TabsContent value="appointments">
              {appointments.length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className="w-12 h-12 text-[#A0AEC0] mx-auto mb-4" />
                  <p className="font-body text-[#4A5568]">No appointments yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Client</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Service</TableHead>
                        <TableHead>Date & Time</TableHead>
                        <TableHead>Calendar</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {appointments.map((apt) => (
                        <TableRow key={apt.id} data-testid={`appointment-row-${apt.id}`}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-[#A0AEC0]" />
                              <span className="font-medium">{apt.client_name}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <div className="flex items-center gap-2 text-sm">
                                <Mail className="w-3 h-3 text-[#A0AEC0]" />
                                <a href={`mailto:${apt.email}`} className="hover:text-[#0F392B]">
                                  {apt.email}
                                </a>
                              </div>
                              <div className="flex items-center gap-2 text-sm">
                                <Phone className="w-3 h-3 text-[#A0AEC0]" />
                                <a href={`tel:${apt.phone}`} className="hover:text-[#0F392B]">
                                  {apt.phone}
                                </a>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#0F392B]/10 text-[#0F392B]">
                              {getServiceName(apt.service)}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <Calendar className="w-3 h-3 text-[#D4AF37]" />
                                <span>{apt.date}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Clock className="w-3 h-3 text-[#D4AF37]" />
                                <span>{apt.time_slot}</span>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {apt.google_event_id ? (
                              <span className="flex items-center gap-1 text-green-600 text-xs">
                                <CheckCircle className="w-3 h-3" />
                                Synced
                              </span>
                            ) : (
                              <span className="flex items-center gap-1 text-[#A0AEC0] text-xs">
                                <XCircle className="w-3 h-3" />
                                Not synced
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteAppointment(apt.id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>

            {/* Tax Intakes Tab */}
            <TabsContent value="intakes">
              {taxIntakes.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-[#A0AEC0] mx-auto mb-4" />
                  <p className="font-body text-[#4A5568]">No tax intakes yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Client</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Filing Status</TableHead>
                        <TableHead>Employment</TableHead>
                        <TableHead>Documents</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {taxIntakes.map((intake) => (
                        <TableRow key={intake.id} data-testid={`intake-row-${intake.id}`}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-[#A0AEC0]" />
                              <span className="font-medium">{intake.full_name}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <div className="flex items-center gap-2 text-sm">
                                <Mail className="w-3 h-3 text-[#A0AEC0]" />
                                <a href={`mailto:${intake.email}`} className="hover:text-[#0F392B]">
                                  {intake.email}
                                </a>
                              </div>
                              <div className="flex items-center gap-2 text-sm">
                                <Phone className="w-3 h-3 text-[#A0AEC0]" />
                                <a href={`tel:${intake.phone}`} className="hover:text-[#0F392B]">
                                  {intake.phone}
                                </a>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm">{getFilingStatusLabel(intake.filing_status)}</span>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm">{getEmploymentTypeLabel(intake.employment_type)}</span>
                          </TableCell>
                          <TableCell>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#D4AF37]/20 text-[#0F392B]">
                              {intake.documents?.length || 0} files
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-[#4A5568]">
                              {intake.created_at ? format(new Date(intake.created_at), "MMM d, yyyy") : "-"}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setSelectedIntake(intake)}
                              className="text-[#0F392B] hover:bg-[#0F392B]/10"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>

            {/* Contacts Tab */}
            <TabsContent value="contacts">
              {contacts.length === 0 ? (
                <div className="text-center py-12">
                  <Mail className="w-12 h-12 text-[#A0AEC0] mx-auto mb-4" />
                  <p className="font-body text-[#4A5568]">No contact inquiries yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Subject</TableHead>
                        <TableHead>Message</TableHead>
                        <TableHead>Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {contacts.map((contact) => (
                        <TableRow key={contact.id} data-testid={`contact-row-${contact.id}`}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-[#A0AEC0]" />
                              <span className="font-medium">{contact.name}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <div className="flex items-center gap-2 text-sm">
                                <Mail className="w-3 h-3 text-[#A0AEC0]" />
                                <a href={`mailto:${contact.email}`} className="hover:text-[#0F392B]">
                                  {contact.email}
                                </a>
                              </div>
                              {contact.phone && (
                                <div className="flex items-center gap-2 text-sm">
                                  <Phone className="w-3 h-3 text-[#A0AEC0]" />
                                  <a href={`tel:${contact.phone}`} className="hover:text-[#0F392B]">
                                    {contact.phone}
                                  </a>
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="font-medium text-[#0F392B]">{contact.subject}</span>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-[#4A5568] max-w-[300px] line-clamp-2 block">
                              {contact.message}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-[#4A5568]">
                              {contact.created_at ? format(new Date(contact.created_at), "MMM d, yyyy") : "-"}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Intake Detail Dialog */}
      <Dialog open={!!selectedIntake} onOpenChange={() => setSelectedIntake(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#0F392B]">
              Tax Intake Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedIntake && (
            <div className="space-y-6">
              <div className="bg-[#F9F9F7] rounded-lg p-4">
                <h3 className="font-heading text-lg font-semibold text-[#0F392B] mb-3 flex items-center gap-2">
                  <User className="w-5 h-5 text-[#D4AF37]" />
                  Client Information
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-[#A0AEC0]">Name:</span>
                    <p className="font-medium">{selectedIntake.full_name}</p>
                  </div>
                  <div>
                    <span className="text-[#A0AEC0]">Email:</span>
                    <p className="font-medium">{selectedIntake.email}</p>
                  </div>
                  <div>
                    <span className="text-[#A0AEC0]">Phone:</span>
                    <p className="font-medium">{selectedIntake.phone}</p>
                  </div>
                  <div>
                    <span className="text-[#A0AEC0]">Address:</span>
                    <p className="font-medium">{selectedIntake.address}</p>
                  </div>
                </div>
              </div>

              <div className="bg-[#F9F9F7] rounded-lg p-4">
                <h3 className="font-heading text-lg font-semibold text-[#0F392B] mb-3 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-[#D4AF37]" />
                  Tax Information
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-[#A0AEC0]">Filing Status:</span>
                    <p className="font-medium">{getFilingStatusLabel(selectedIntake.filing_status)}</p>
                  </div>
                  <div>
                    <span className="text-[#A0AEC0]">Employment Type:</span>
                    <p className="font-medium">{getEmploymentTypeLabel(selectedIntake.employment_type)}</p>
                  </div>
                  <div>
                    <span className="text-[#A0AEC0]">Has Dependents:</span>
                    <p className="font-medium">
                      {selectedIntake.has_dependents ? `Yes (${selectedIntake.dependents_count})` : "No"}
                    </p>
                  </div>
                  <div>
                    <span className="text-[#A0AEC0]">Business Owner:</span>
                    <p className="font-medium">{selectedIntake.is_business_owner ? "Yes" : "No"}</p>
                  </div>
                  <div>
                    <span className="text-[#A0AEC0]">Filed Last Year:</span>
                    <p className="font-medium">{selectedIntake.filed_last_year ? "Yes" : "No"}</p>
                  </div>
                </div>
                {selectedIntake.notes && (
                  <div className="mt-4">
                    <span className="text-[#A0AEC0]">Notes:</span>
                    <p className="font-medium mt-1">{selectedIntake.notes}</p>
                  </div>
                )}
              </div>

              {selectedIntake.documents && selectedIntake.documents.length > 0 && (
                <div className="bg-[#F9F9F7] rounded-lg p-4">
                  <h3 className="font-heading text-lg font-semibold text-[#0F392B] mb-3 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#D4AF37]" />
                    Uploaded Documents
                  </h3>
                  <div className="space-y-2">
                    {selectedIntake.documents.map((doc, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between bg-white rounded-lg p-3"
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-[#0F392B]" />
                          <span className="text-sm">{doc.original_name}</span>
                        </div>
                        <a
                          href={`${API}/documents/${doc.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button variant="ghost" size="sm" className="text-[#0F392B]">
                            <Download className="w-4 h-4" />
                          </Button>
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
